import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getRecycleBin,
  permanentDeleteFile,
  permanentDeleteFolder,
  recycleBinKeys,
  restoreFile,
  restoreFolder,
} from '../api/recycleBinApi';
import { folderContentsKeys } from './useFolderContents';
import { siteFilesKeys } from './useSiteFiles';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';

export function useRecycleBin(siteId?: number) {
  return useQuery({
    queryKey: recycleBinKeys.list(siteId),
    queryFn: () => getRecycleBin(siteId),
  });
}

export function useRestoreFile(siteId?: number) {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  return useMutation({
    mutationFn: ({
      fileId,
      body,
    }: {
      fileId: string;
      body?: { newName?: string; replaceFileId?: string };
    }) => restoreFile(fileId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.all });
      queryClient.invalidateQueries({ queryKey: folderContentsKeys.all });
      if (siteId) {
        queryClient.invalidateQueries({
          queryKey: siteFilesKeys.list(siteId),
        });
      }
      showSnackbar('File restored', 'success');
    },
  });
}

export function useRestoreFolder(siteId?: number) {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  return useMutation({
    mutationFn: (folderId: string) => restoreFolder(folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.all });
      queryClient.invalidateQueries({ queryKey: folderContentsKeys.all });
      if (siteId) {
        queryClient.invalidateQueries({
          queryKey: siteFilesKeys.list(siteId),
        });
      }
      showSnackbar('Folder restored', 'success');
    },
  });
}

export function usePermanentDeleteFile(siteId?: number) {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  return useMutation({
    mutationFn: (fileId: string) => permanentDeleteFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.list(siteId) });
      showSnackbar('File permanently deleted', 'success');
    },
  });
}

export function usePermanentDeleteFolder(siteId?: number) {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  return useMutation({
    mutationFn: (folderId: string) => permanentDeleteFolder(folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recycleBinKeys.list(siteId) });
      showSnackbar('Folder permanently deleted', 'success');
    },
  });
}
